import type { LabelPositionConfig } from './types/destinations';
import { icons } from '@images';

/**
 * Globe Configuration Constants
 *
 * Color palette and settings matching the design mockup
 */

// Globe configuration
export const GLOBE_CONFIG = {
  // Visual settings
  showAtmosphere: false, // Flat design, no atmosphere glow
  showGraticules: false, // No latitude/longitude lines

  // Marker settings
  pointAltitude: 0.01, // Low elevation for flat markers
  pointRadius: 0.5, // Marker size
  pointResolution: 12, // Marker quality (lower = better performance)

  // Camera settings
  cameraDistance: 2.5, // Initial zoom level (altitude)
  cameraRotationSpeed: 0.5, // Auto-rotation speed (if enabled)

  // Zoom limits (altitude-based: lower = zoomed in, higher = zoomed out)
  minZoom: 2.5, // Maximum zoom in (minimum altitude)
  maxZoom: 4.0, // Maximum zoom out (maximum altitude)

  // Animation
  animateIn: true, // Fade in on load
  animationDuration: 1000, // Transition duration (ms)
} as const;

// Texture options
export const GLOBE_TEXTURES = {
  // Realistic textures
  blueMarble: '//unpkg.com/three-globe/example/img/earth-blue-marble.jpg',
  nightLights: '//unpkg.com/three-globe/example/img/earth-night.jpg',

  // Flat/minimalist textures (recommended for mockup)
  topology: '//unpkg.com/three-globe/example/img/earth-topology.png',
  day: '//unpkg.com/three-globe/example/img/earth-day.jpg',

  // Custom texture (land: #d0ecfa, water: #e8f6fd)
  custom: icons.EarthCustomIcon,
} as const;

// Background options
export const GLOBE_BACKGROUNDS = {
  white: 'rgba(255, 255, 255, 1)',
  transparent: 'rgba(0, 0, 0, 0)',
  nightSky: '//unpkg.com/three-globe/example/img/night-sky.png',
} as const;

// Animation configuration for cycling tooltips
export const TOOLTIP_ANIMATION_CONFIG = {
  initialDelay: 500,
  cycleDuration: 2000,
} as const;

// Label positioning configuration - customize position for specific cities
export const LABEL_POSITION_CONFIG: Record<string, LabelPositionConfig> = {} as const;

// Default label position
export const DEFAULT_LABEL_POSITION: LabelPositionConfig = {
  top: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
} as const;

// Zoom level configuration for destination visibility
// Camera altitude ranges (lower = zoomed in, higher = zoomed out)
export const ZOOM_LEVELS = {
  // Show all destinations (priority 1, 2, 3)
  close: 1.5, // altitude <= 1.5 (very zoomed in)

  // Show major + popular destinations (priority 1, 2)
  medium: 2.3, // altitude <= 2.3 (medium zoom)

  // Show only major destinations (priority 1)
  far: Number.POSITIVE_INFINITY, // altitude > 2.3 (zoomed out)
} as const;

// Helper function to get visible destinations based on zoom level
export function getVisibleDestinationsByZoom(altitude: number): number[] {
  if (altitude <= ZOOM_LEVELS.close) {
    return [1, 2, 3]; // Show all
  }
  if (altitude <= ZOOM_LEVELS.medium) {
    return [1, 2]; // Show major + popular
  }
  return [1]; // Show only major
}

/**

 */
export const GOOGLE_MAPS_CONFIG = {
  apiKey: 'AIzaSyDWFcIeuHc7qO7NDPPY8gLIMXd7u4ldlYk',
  mapId: 'itinerary-map', // Optional: for advanced styling
  libraries: ['marker', 'geometry'] as const,
} as const;

/**
 * Google Maps JS API loader: India regional bias only (no fixed `language` — follows browser).
 * @see https://developers.google.com/maps/documentation/javascript/localization
 */
export const GOOGLE_MAPS_LOADER_LOCALE = {
  region: 'IN' as const,
} as const;

// Route styling constants matching Figma design
export const ROUTE_STYLE = {
  strokeColor: '#284CFF',
  strokeWeight: 5,
  strokeOpacity: 1,
  geodesic: true,
} as const;

// Default curve intensity for route polylines (0 = straight, higher = more curved)
export const DEFAULT_CURVE_INTENSITY = 0.3;

// Default zoom level for single destination (city-level view)
export const DEFAULT_SINGLE_STOP_ZOOM = 8;

// Default padding for map bounds fitting
export const DEFAULT_MAP_PADDING = 50;

// Default zoom level for itinerary map
export const DEFAULT_ITINERARY_ZOOM = 4;

// Marker gradient border from Figma
export const MARKER_GRADIENT =
  'linear-gradient(20deg, rgba(255, 234, 94, 1) 0%, rgba(142, 255, 105, 1) 50%, rgba(80, 176, 255, 1) 100%)';

// Onboarding chat input: sample prompts shown with typing animation
export const SAMPLE_PROMPTS = [
  'Find me the cheapest flight from Delhi to Bangkok next weekend',
  'Show me non-stop flights from Delhi to London',
  'Find morning flights from Bangalore to Mumbai tomorrow, returning the same evening.',
  'Show me flights from Hyderabad to San Francisco with a short layover under 4 hours.',
  'Show me evening flights from Delhi to Goa next Friday for a weekend trip.',
  'Show me IndiGo flights from Chennai to Bangalore tomorrow under ₹7,000.',
] as const;

// Chat input: maximum word count limit for user messages
export const MAX_WORD_COUNT = 100;

// Onboarding chat input: typing animation speed (ms)
export const TYPING_ANIMATION_CONFIG = {
  typingSpeed: 40, // ms per character when typing
  deletingSpeed: 25, // ms per character when deleting
  pauseAfterTyped: 2000, // pause after fully typed before deleting
  pauseAfterDeleted: 500, // pause after fully deleted before next prompt
} as const;
